import {
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { buildGuestRegistrationLink, buildQrImageUrl } from "../../services/EventMasterService";

const GA_COLORS = {
  primary: "#8a5200",
  primaryDark: "#5a3600",
  accent: "#1e8a5a",
  accentDark: "#13613f",
  danger: "#b02a37",
  dangerDark: "#7f1d28",
  border: "#c27a2a",
  heading: "#4a2400",
};

const actionButtonSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontWeight: 700,
};

const gaFormSx = {
  "& .MuiInputLabel-root": {
    color: "#7a4a15",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: GA_COLORS.primary,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#d3b994",
    },
    "&:hover fieldset": {
      borderColor: "#b9874e",
    },
    "&.Mui-focused fieldset": {
      borderColor: GA_COLORS.primary,
      borderWidth: "1px",
    },
  },
};

function EventDetailScreen({
  isEditing,
  selectedEvent,
  mode,
  loading,
  canCreate,
  canEdit,
  canDelete,
  form,
  typeOptions,
  statusOptions,
  onBackToList,
  onCopy,
  onModify,
  onToggleStatus,
  onOpenBrochure,
  onPrintEvent,
  onPrintQrCode,
  onPrintRegistrationIds,
  onCopyGuestLink,
  onDelete,
  onCancel,
  onSubmit,
  onFieldChange,
  onAgeFieldChange,
  onAddAgeGroup,
  onRemoveAgeGroup,
  onPaymentModeFieldChange,
  onPaymentModeAttributeChange,
  onAddPaymentMode,
  onRemovePaymentMode,
}) {
  const [showMultiPrintDialog, setShowMultiPrintDialog] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);

  const guestLink = selectedEvent?.eventID
    ? buildGuestRegistrationLink(selectedEvent.eventID)
    : "";

  const ActionBar = () => (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
      {!isEditing && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={onBackToList}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Back
          </Button>
          {selectedEvent && canCreate && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onCopy?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              Copy
            </Button>
          )}
          {selectedEvent && canEdit && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onToggleStatus?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              {selectedEvent.status === "Confirmed" ? "Close" : "Confirm"}
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onPrintEvent?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              Print
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowQrDialog(true)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              QR Code
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowMultiPrintDialog(true)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              Multi Print IDs
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onCopyGuestLink?.(selectedEvent)}
              disabled={
                loading ||
                !selectedEvent.onlineRegRequired ||
                selectedEvent.status !== "Confirmed"
              }
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              Copy Guest Link
            </Button>
          )}
          {selectedEvent && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onOpenBrochure?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
            >
              {selectedEvent.brochurePath ? "Brochure" : "Upload Brochure"}
            </Button>
          )}
          {selectedEvent && canEdit && (
            <Button
              variant="contained"
              size="small"
              onClick={() => onModify?.(selectedEvent)}
              sx={{ ...actionButtonSx, bgcolor: GA_COLORS.accent, "&:hover": { bgcolor: GA_COLORS.accentDark } }}
            >
              Edit
            </Button>
          )}
          {selectedEvent && canDelete && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onDelete?.(selectedEvent)}
              sx={{ ...actionButtonSx, borderColor: GA_COLORS.danger, color: GA_COLORS.danger }}
            >
              Delete
            </Button>
          )}
        </>
      )}
      {isEditing && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={onBackToList}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="small"
            type="submit"
            form="event-master-form"
            disabled={loading}
            sx={{ ...actionButtonSx, bgcolor: GA_COLORS.accent, "&:hover": { bgcolor: GA_COLORS.accentDark } }}
          >
            {loading ? "Saving..." : mode === "create" ? "Create" : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Cancel
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <Paper
      elevation={2}
      sx={{ p: 2, border: `1px solid ${GA_COLORS.border}`, backgroundColor: "#fffdf9" }}
    >
      <ActionBar />
      <Divider sx={{ my: 2 }} />

      <Typography variant="overline" color="#8a5200" display="block">
        Event Master
      </Typography>
      <Typography variant="h5" sx={{ mb: 2, color: GA_COLORS.heading, fontWeight: 700 }}>
        {isEditing
          ? mode === "create"
            ? "Create Event"
            : "Edit Event"
          : selectedEvent?.name || "Event Details"}
      </Typography>

      <Box component="form" id="event-master-form" onSubmit={onSubmit} sx={gaFormSx}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name"
              value={form.name}
              onChange={(e) => onFieldChange("name", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Type"
              select
              value={form.type}
              onChange={(e) => onFieldChange("type", e.target.value)}
              disabled={!isEditing}
            >
              {typeOptions.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Status"
              select
              value={form.status}
              onChange={(e) => onFieldChange("status", e.target.value)}
              disabled={!isEditing}
            >
              {statusOptions.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Reg Start"
              InputLabelProps={{ shrink: true }}
              value={form.regDate}
              onChange={(e) => onFieldChange("regDate", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Reg End"
              InputLabelProps={{ shrink: true }}
              value={form.regEndDate}
              onChange={(e) => onFieldChange("regEndDate", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Event Start"
              InputLabelProps={{ shrink: true }}
              value={form.startDate}
              onChange={(e) => onFieldChange("startDate", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Event End"
              InputLabelProps={{ shrink: true }}
              value={form.endDate}
              onChange={(e) => onFieldChange("endDate", e.target.value)}
              disabled={!isEditing}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Short Description"
              value={form.shortDescription}
              onChange={(e) => onFieldChange("shortDescription", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Description"
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              disabled={!isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, color: GA_COLORS.heading, fontWeight: 700 }}>
              Age Groups
            </Typography>
          </Grid>

          {(form.ageGroups || []).map((ageGroup, index) => (
            <Grid item xs={12} key={ageGroup._key || index}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: "#d9b48b", backgroundColor: "#fffefc" }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={ageGroup.ageGroupName || ""}
                      onChange={(e) =>
                        onAgeFieldChange(index, "ageGroupName", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Min Age"
                      value={ageGroup.minAge || 0}
                      onChange={(e) => onAgeFieldChange(index, "minAge", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Age"
                      value={ageGroup.maxAge || 0}
                      onChange={(e) => onAgeFieldChange(index, "maxAge", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Rate"
                      value={ageGroup.rate || 0}
                      onChange={(e) => onAgeFieldChange(index, "rate", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Count"
                      value={ageGroup.maxCount || ""}
                      onChange={(e) => onAgeFieldChange(index, "maxCount", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Waitlist"
                      value={ageGroup.waitlistCount || ""}
                      onChange={(e) => onAgeFieldChange(index, "waitlistCount", e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    {isEditing && (
                      <Button
                        color="error"
                        onClick={() => onRemoveAgeGroup(index)}
                        disabled={loading}
                        sx={{ ...actionButtonSx }}
                      >
                        Remove
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}

          {isEditing && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={onAddAgeGroup}
                sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
              >
                Add Age Group
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, color: GA_COLORS.heading, fontWeight: 700 }}>
              Payment Modes
            </Typography>
          </Grid>

          {(form.paymentModes || []).map((paymentMode, index) => (
            <Grid item xs={12} key={paymentMode._key || index}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: "#d9b48b", backgroundColor: "#fffefc" }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Mode Type"
                      value={paymentMode.modeType || ""}
                      onChange={(e) =>
                        onPaymentModeFieldChange(index, "modeType", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Display Name"
                      value={paymentMode.displayName || ""}
                      onChange={(e) =>
                        onPaymentModeFieldChange(index, "displayName", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Instructions"
                      value={paymentMode.modeAttributes?.instructions || ""}
                      onChange={(e) =>
                        onPaymentModeAttributeChange(
                          index,
                          "instructions",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    {isEditing && (
                      <Button
                        color="error"
                        onClick={() => onRemovePaymentMode(index)}
                        disabled={loading}
                        sx={{ ...actionButtonSx }}
                      >
                        Remove
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}

          {isEditing && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={onAddPaymentMode}
                sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
              >
                Add Payment Mode
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      <Dialog
        open={showQrDialog}
        onClose={() => setShowQrDialog(false)}
        PaperProps={{ sx: { border: `1px solid ${GA_COLORS.border}` } }}
      >
        <DialogTitle>Guest Registration QR</DialogTitle>
        <DialogContent>
          {selectedEvent?.eventID ? (
            <Stack spacing={1} alignItems="center" sx={{ minWidth: 280 }}>
              <img
                src={buildQrImageUrl(guestLink)}
                alt="Guest registration QR code"
                style={{ width: 240, height: 240 }}
              />
              <Typography variant="body2" color="text.secondary">
                {guestLink}
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => onCopyGuestLink?.(selectedEvent)}
            disabled={!selectedEvent?.eventID}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Copy Link
          </Button>
          <Button
            variant="outlined"
            onClick={() => window.open(buildQrImageUrl(guestLink), "_blank")}
            disabled={!selectedEvent?.eventID}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Open QR
          </Button>
          <Button
            variant="outlined"
            onClick={() => onPrintQrCode?.(selectedEvent)}
            disabled={!selectedEvent?.eventID}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            Print QR
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowQrDialog(false)}
            sx={{ ...actionButtonSx, bgcolor: GA_COLORS.accent, "&:hover": { bgcolor: GA_COLORS.accentDark } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showMultiPrintDialog}
        onClose={() => setShowMultiPrintDialog(false)}
        PaperProps={{ sx: { border: `1px solid ${GA_COLORS.border}` } }}
      >
        <DialogTitle>ID Label Printing</DialogTitle>
        <DialogContent>
          <Typography>
            Choose which registrations to print for{" "}
            <strong>{selectedEvent?.name}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => onPrintRegistrationIds?.(selectedEvent, "male")}
            disabled={loading || !selectedEvent}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            IDs for Male
          </Button>
          <Button
            variant="outlined"
            onClick={() => onPrintRegistrationIds?.(selectedEvent, "female")}
            disabled={loading || !selectedEvent}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            IDs for Female
          </Button>
          <Button
            variant="outlined"
            onClick={() => onPrintRegistrationIds?.(selectedEvent, "all")}
            disabled={loading || !selectedEvent}
            sx={{ ...actionButtonSx, borderColor: GA_COLORS.primary, color: GA_COLORS.primaryDark }}
          >
            IDs for All
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowMultiPrintDialog(false)}
            sx={{ ...actionButtonSx, bgcolor: GA_COLORS.accent, "&:hover": { bgcolor: GA_COLORS.accentDark } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default EventDetailScreen;
